import { z } from 'zod';
import { formatBrl } from './money.js';
import type { TicketValidation } from './pricing.js';
import { isValidTaxId, normalizeTaxId } from './tax-id.js';

const MINUTES = 'Informe de 1 a 1.440 minutos, sem casas decimais.';
const minutesSchema = z.number({ error: MINUTES }).int(MINUTES).min(1, MINUTES).max(1440, MINUTES);
const PERCENTAGE = 'Informe de 1 a 100%, sem casas decimais.';
/** Ausente é `restart`: a loja paga como a tabela cobra, recomeçando à meia-noite (#580). */
const midnightSchema = z.enum(['restart', 'continue']).optional();
const STORE = 'Informe o valor que a loja paga por ticket.';
/**
 * O que a loja paga ao estacionamento por ticket validado, à parte do que o
 * cliente ganha (#584). Ausente, a loja paga o desconto do cliente.
 */
const storeCentsSchema = z.number({ error: STORE }).int(STORE).min(0, STORE).optional();

/**
 * Acordo da loja conveniada (#533): o que a loja paga do estacionamento de
 * quem compra nela. É o dono que combina; quem valida pela loja só aplica.
 */
export const partnerAgreementSchema = z.discriminatedUnion('kind', [
  /** A loja paga o que os primeiros minutos custam na tabela do lote (#532). */
  z.object({
    kind: z.literal('covered_time'),
    minutes: minutesSchema,
    midnight: midnightSchema,
    storeCents: storeCentsSchema,
  }),
  z.object({
    kind: z.literal('fixed_discount'),
    valueCents: z
      .number({ error: 'Informe o valor.' })
      .int()
      .positive('Informe um valor maior que zero.'),
    storeCents: storeCentsSchema,
  }),
  z.object({
    kind: z.literal('pct_discount'),
    percentage: z
      .number({ error: PERCENTAGE })
      .int(PERCENTAGE)
      .min(1, PERCENTAGE)
      .max(100, PERCENTAGE),
    storeCents: storeCentsSchema,
  }),
  z.object({ kind: z.literal('full'), storeCents: storeCentsSchema }),
  /**
   * Preço do convênio: o cliente da loja paga um valor próprio pelos primeiros
   * minutos, a loja paga a diferença para a tabela, e o que passar segue a
   * tabela. Nunca cobra mais que a tabela.
   */
  z.object({
    kind: z.literal('driver_price'),
    minutes: minutesSchema,
    valueCents: z
      .number({ error: 'Informe o preço.' })
      .int()
      .positive(
        'Informe um preço maior que zero. Para o cliente não pagar nada, use Primeiras horas.',
      ),
    midnight: midnightSchema,
    storeCents: storeCentsSchema,
  }),
]);

export type PartnerAgreement = z.infer<typeof partnerAgreementSchema>;

export const PARTNER_NAME_MAX = 80;

export const partnerInputSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Informe o nome da loja.')
    .max(PARTNER_NAME_MAX, `Use até ${PARTNER_NAME_MAX} caracteres.`),
  /**
   * CNPJ ou CPF da loja, para o dono cobrar. Ficam só os dígitos, e as letras
   * do CNPJ alfanumérico; o dígito verificador tem que bater (#546).
   */
  document: z
    .string()
    .transform(normalizeTaxId)
    .refine((v) => v === '' || isValidTaxId(v), 'CPF ou CNPJ inválido. Confira os números.')
    .transform((v) => (v === '' ? null : v))
    .nullish(),
  agreement: partnerAgreementSchema,
});

export type PartnerInput = z.infer<typeof partnerInputSchema>;

/**
 * Nome para comparar lojas do mesmo lote (#546): duas ativas com o mesmo nome
 * confundem quem escolhe a loja no ticket e quem lê o extrato. Caixa, acento e
 * espaço a mais não contam.
 */
export const partnerNameKey = (name: string): string =>
  name.normalize('NFD').replace(/\p{M}/gu, '').trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * O acordo como validação do ticket, com o nome da loja na linha do recibo. O
 * que a loja paga fica fora: não mexe no preço do cliente (#584).
 */
export const partnerValidation = (name: string, agreement: PartnerAgreement): TicketValidation => {
  const { storeCents: _store, ...customer } = agreement;
  return { ...customer, label: `Convênio ${name}` };
};

const hours = (minutes: number): string => {
  if (minutes === 60) return 'a primeira hora';
  if (minutes % 60 === 0) return `as ${minutes / 60} primeiras horas`;
  return `os primeiros ${minutes} min`;
};

const forHours = (minutes: number): string => {
  if (minutes === 60) return 'pela primeira hora';
  if (minutes % 60 === 0) return `pelas ${minutes / 60} primeiras horas`;
  return `pelos primeiros ${minutes} min`;
};

/** Só aparece quando o dono tirou a virada da meia-noite da parte da loja (#580). */
const noRestart = (agreement: { midnight?: 'restart' | 'continue' }) =>
  agreement.midnight === 'continue' ? ', sem a tabela recomeçar à meia-noite' : '';

/** O lado do cliente, quando a loja paga um valor combinado à parte (#584). */
const customerText = (agreement: PartnerAgreement): string => {
  switch (agreement.kind) {
    case 'covered_time':
      return `O cliente ganha ${hours(agreement.minutes)}${noRestart(agreement)} e paga o que passar.`;
    case 'fixed_discount':
      return `O cliente ganha até ${formatBrl(agreement.valueCents)} de desconto.`;
    case 'pct_discount':
      return `O cliente ganha ${agreement.percentage}% de desconto na estadia.`;
    case 'full':
      return 'O cliente não paga a estadia.';
    case 'driver_price':
      return `O cliente paga ${formatBrl(agreement.valueCents)} ${forHours(agreement.minutes)}${noRestart(agreement)}; depois, paga a tabela.`;
  }
};

const storeText = (cents: number): string =>
  cents === 0 ? 'A loja não paga por ticket.' : `A loja paga ${formatBrl(cents)} por ticket.`;

/** O acordo numa frase para o dono e para o caixa da loja. */
export const agreementText = (agreement: PartnerAgreement): string => {
  if (agreement.storeCents !== undefined) {
    return `${customerText(agreement)} ${storeText(agreement.storeCents)}`;
  }
  switch (agreement.kind) {
    case 'covered_time':
      return `A loja paga ${hours(agreement.minutes)}${noRestart(agreement)}; o cliente paga o que passar.`;
    case 'fixed_discount':
      return `A loja paga até ${formatBrl(agreement.valueCents)}; o cliente paga o que passar.`;
    case 'pct_discount':
      return `A loja paga ${agreement.percentage}% da estadia.`;
    case 'full':
      return 'A loja paga a estadia inteira.';
    case 'driver_price':
      return `O cliente paga ${formatBrl(agreement.valueCents)} ${forHours(agreement.minutes)} e a loja paga a diferença${noRestart(agreement)}; depois, o cliente paga a tabela.`;
  }
};

/**
 * Quanto o convênio custa ao estacionamento (#584): o desconto dos clientes
 * menos o que as lojas pagam. Sem valor combinado os dois batem e não há o que
 * dizer.
 */
export const concessionText = (discountCents: number, dueCents: number): string | null => {
  if (discountCents === dueCents) return null;
  const discount = `Os clientes ganharam ${formatBrl(discountCents)} de desconto`;
  return discountCents > dueCents
    ? `${discount}; o estacionamento banca ${formatBrl(discountCents - dueCents)}.`
    : `${discount}; a loja paga ${formatBrl(dueCents - discountCents)} a mais.`;
};
